/**
 * AccordionExamples.tsx
 * Comprehensive examples of HeroUI Accordion component
 * 
 * Showcases all HeroUI Accordion features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Accordion, AccordionItem } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';
import { 
  TrendingUp, 
  BarChart3,
  Settings,
  HelpCircle,
  FileText,
  DollarSign,
  Calendar,
  Bell,
  Lock,
  Zap,
  BookOpen,
  Target,
  LineChart
} from 'lucide-react';

export const AccordionExamples: React.FC = () => {
  const [controlledKeys, setControlledKeys] = useState<string[]>(['controlled-1']);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Accordion Variants */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Accordion Variants</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Light Variant (Default) */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Light (Default)</h4>
                <Accordion selectionMode="single">
                  <AccordionItem key="1" title="Trading Strategy">
                    Your primary trading strategy and approach to the markets.
                  </AccordionItem>
                  <AccordionItem key="2" title="Risk Management">
                    Position sizing, stop losses, and portfolio risk controls.
                  </AccordionItem>
                  <AccordionItem key="3" title="Market Analysis">
                    Technical and fundamental analysis methodologies.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

            {/* Shadow Variant */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Shadow</h4>
                <Accordion variant="shadow" selectionMode="single">
                  <AccordionItem key="1" title="Performance Metrics">
                    Track your trading performance and key metrics.
                  </AccordionItem>
                  <AccordionItem key="2" title="Trade Journal">
                    Detailed records of all your trading activities.
                  </AccordionItem>
                  <AccordionItem key="3" title="Analytics">
                    Advanced analytics and reporting features.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

            {/* Bordered Variant */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Bordered</h4>
                <Accordion variant="bordered" selectionMode="single">
                  <AccordionItem key="1" title="Account Setup">
                    Configure your trading account and preferences.
                  </AccordionItem>
                  <AccordionItem key="2" title="Broker Integration">
                    Connect with your broker for live data feeds.
                  </AccordionItem>
                  <AccordionItem key="3" title="Data Export">
                    Export your data for external analysis.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

            {/* Splitted Variant */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Splitted</h4>
                <Accordion variant="splitted" selectionMode="single">
                  <AccordionItem key="1" title="Market Data">
                    Real-time and historical market data access.
                  </AccordionItem>
                  <AccordionItem key="2" title="Alerts & Notifications">
                    Set up custom alerts for market movements.
                  </AccordionItem>
                  <AccordionItem key="3" title="Portfolio Tracking">
                    Monitor your overall portfolio performance.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Selection Modes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Selection Modes</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Single Selection */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Single Selection</h4>
                <Accordion selectionMode="single" variant="bordered">
                  <AccordionItem key="single-1" title="Current Trade" subtitle="Active position">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Symbol:</span>
                        <Badge color="primary">AAPL</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>P&L:</span>
                        <span className="text-green-600 font-medium">+$1,250</span>
                      </div>
                      <Button size="sm" color="primary">View Details</Button>
                    </div>
                  </AccordionItem>
                  <AccordionItem key="single-2" title="Recent Trades" subtitle="Last 10 trades">
                    View your most recent trading activity and performance metrics.
                  </AccordionItem>
                  <AccordionItem key="single-3" title="Watchlist" subtitle="Monitored symbols">
                    Symbols you're tracking for potential trading opportunities.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

            {/* Multiple Selection */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Multiple Selection</h4>
                <Accordion selectionMode="multiple" variant="bordered">
                  <AccordionItem key="multi-1" title="Technical Indicators">
                    <div className="grid grid-cols-2 gap-2">
                      <Chip size="sm" color="primary">RSI</Chip>
                      <Chip size="sm" color="primary">MACD</Chip>
                      <Chip size="sm" color="secondary">Bollinger Bands</Chip>
                      <Chip size="sm" color="secondary">Moving Average</Chip>
                    </div>
                  </AccordionItem>
                  <AccordionItem key="multi-2" title="Chart Patterns">
                    <div className="space-y-2">
                      <p className="text-sm text-text-secondary">Identified patterns in current timeframe</p>
                      <div className="flex gap-2">
                        <Badge color="success">Head & Shoulders</Badge>
                        <Badge color="warning">Triangle</Badge>
                      </div>
                    </div>
                  </AccordionItem>
                  <AccordionItem key="multi-3" title="Market Sentiment">
                    Aggregate sentiment analysis from multiple data sources.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Advanced Features */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Features</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Compact Mode */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Compact Mode</h4>
                <Accordion isCompact variant="shadow" selectionMode="single">
                  <AccordionItem key="compact-1" title="Quick Stats" startContent={<BarChart3 className="w-4 h-4" />}>
                    Condensed view of key performance metrics.
                  </AccordionItem>
                  <AccordionItem key="compact-2" title="Alerts" startContent={<Bell className="w-4 h-4" />}>
                    Active alerts and notification settings.
                  </AccordionItem>
                  <AccordionItem key="compact-3" title="Settings" startContent={<Settings className="w-4 h-4" />}>
                    Quick access to important configuration options.
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

            {/* With Default Expanded Items */}
            <Card>
              <CardBody>
                <h4 className="font-medium text-text-primary mb-3">Default Expanded</h4>
                <Accordion 
                  selectionMode="multiple" 
                  variant="bordered"
                  defaultExpandedKeys={["expanded-1", "expanded-3"]}
                >
                  <AccordionItem key="expanded-1" title="Portfolio Summary" startContent={<DollarSign className="w-4 h-4" />}>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Total Value:</span>
                        <span className="font-medium">$125,432</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Today's P&L:</span>
                        <span className="text-green-600">+$2,341</span>
                      </div>
                    </div>
                  </AccordionItem>
                  <AccordionItem key="expanded-2" title="Trading Rules" startContent={<BookOpen className="w-4 h-4" />}>
                    Your personalized trading rules and guidelines.
                  </AccordionItem>
                  <AccordionItem key="expanded-3" title="Goals & Targets" startContent={<Target className="w-4 h-4" />}>
                    <div className="space-y-2">
                      <p className="text-sm">Monthly target: $5,000</p>
                      <div className="flex justify-between">
                        <span>Progress:</span>
                        <Badge color="success">67%</Badge>
                      </div>
                    </div>
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Controlled Accordion */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Controlled Accordion</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    color="primary"
                    onPress={() => setControlledKeys(['controlled-1', 'controlled-2'])}
                  >
                    Expand Trading & Analysis
                  </Button>
                  <Button 
                    size="sm" 
                    color="secondary"
                    onPress={() => setControlledKeys(['controlled-3'])}
                  >
                    Expand Reports Only
                  </Button>
                  <Button 
                    size="sm" 
                    variant="bordered"
                    onPress={() => setControlledKeys([])}
                  >
                    Collapse All
                  </Button>
                </div>
                
                <Accordion 
                  selectionMode="multiple" 
                  variant="bordered"
                  selectedKeys={controlledKeys}
                  onSelectionChange={(keys) => setControlledKeys(Array.from(keys) as string[])}
                >
                  <AccordionItem 
                    key="controlled-1" 
                    title="Trading Dashboard" 
                    subtitle="Real-time trading data"
                    startContent={<TrendingUp className="w-4 h-4" />}
                  >
                    <div className="space-y-3">
                      <p>Live market data, active positions, and real-time P&L tracking.</p>
                      <div className="flex gap-2">
                        <Button size="sm" color="primary" startContent={<LineChart className="w-3 h-3" />}>
                          View Charts
                        </Button>
                        <Button size="sm" variant="bordered" startContent={<Calendar className="w-3 h-3" />}>
                          Schedule
                        </Button>
                      </div>
                    </div>
                  </AccordionItem>
                  
                  <AccordionItem 
                    key="controlled-2" 
                    title="Market Analysis" 
                    subtitle="Technical & fundamental"
                    startContent={<BarChart3 className="w-4 h-4" />}
                  >
                    Comprehensive market analysis tools including technical indicators, chart patterns, and fundamental data.
                  </AccordionItem>
                  
                  <AccordionItem 
                    key="controlled-3" 
                    title="Performance Reports" 
                    subtitle="Analytics & insights"
                    startContent={<FileText className="w-4 h-4" />}
                  >
                    <div className="space-y-2">
                      <p>Detailed performance analytics and trading insights.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Badge color="success" variant="flat">Win Rate: 68%</Badge>
                        <Badge color="primary" variant="flat">Avg Win: $450</Badge>
                        <Badge color="warning" variant="flat">Avg Loss: $280</Badge>
                        <Badge color="secondary" variant="flat">Profit Factor: 2.1</Badge>
                      </div>
                    </div>
                  </AccordionItem>
                </Accordion>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Disabled Items */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Disabled Items</h3>
          <Card>
            <CardBody>
              <Accordion selectionMode="single" variant="bordered">
                <AccordionItem key="enabled-1" title="Basic Features" subtitle="Always available">
                  Core trading journal functionality available to all users.
                </AccordionItem>
                
                <AccordionItem 
                  key="disabled-1" 
                  title="Advanced Analytics" 
                  subtitle="Pro feature"
                  isDisabled
                  startContent={<Zap className="w-4 h-4" />}
                >
                  Advanced analytics features require a Pro subscription.
                </AccordionItem>
                
                <AccordionItem key="enabled-2" title="Export Data" subtitle="CSV, PDF formats">
                  Export your trading data in various formats.
                </AccordionItem>
                
                <AccordionItem 
                  key="disabled-2" 
                  title="API Access" 
                  subtitle="Enterprise only"
                  isDisabled
                  startContent={<Lock className="w-4 h-4" />}
                >
                  API access is available for Enterprise customers only.
                </AccordionItem>
              </Accordion>
            </CardBody>
          </Card>
        </div>

        {/* FAQ Style Accordion */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">FAQ Style</h3>
          <Card>
            <CardBody>
              <Accordion selectionMode="single" variant="splitted">
                <AccordionItem 
                  key="faq-1" 
                  title="How do I start tracking my trades?" 
                  startContent={<HelpCircle className="w-4 h-4" />}
                >
                  <div className="space-y-2">
                    <p>To start tracking your trades:</p>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-text-secondary ml-4">
                      <li>Set up your trading account in Settings</li>
                      <li>Connect your broker for live data (optional)</li>
                      <li>Create your first trading session</li>
                      <li>Begin logging trades with our simple form</li>
                    </ol>
                    <Button size="sm" color="primary">Get Started</Button>
                  </div>
                </AccordionItem>
                
                <AccordionItem 
                  key="faq-2" 
                  title="What metrics does the app track?" 
                  startContent={<HelpCircle className="w-4 h-4" />}
                >
                  We track comprehensive trading metrics including P&L, win rate, average win/loss, profit factor, Sharpe ratio, maximum drawdown, and many more professional trading statistics.
                </AccordionItem>
                
                <AccordionItem 
                  key="faq-3" 
                  title="Can I export my trading data?" 
                  startContent={<HelpCircle className="w-4 h-4" />}
                >
                  <div className="space-y-2">
                    <p>Yes! You can export your data in multiple formats:</p>
                    <div className="flex gap-2">
                      <Chip size="sm" color="primary">CSV</Chip>
                      <Chip size="sm" color="primary">JSON</Chip>
                      <Chip size="sm" color="secondary">PDF Reports</Chip>
                    </div>
                  </div>
                </AccordionItem>
                
                <AccordionItem 
                  key="faq-4" 
                  title="Is my trading data secure?" 
                  startContent={<HelpCircle className="w-4 h-4" />}
                >
                  <div className="space-y-2">
                    <p>Absolutely. Your trading data is:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text-secondary ml-4">
                      <li>Stored locally on your device by default</li>
                      <li>Encrypted when using cloud sync (Pro feature)</li>
                      <li>Never shared with third parties</li>
                      <li>Protected by industry-standard security measures</li>
                    </ul>
                  </div>
                </AccordionItem>
              </Accordion>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};